'use client'

import { usePathname, useRouter } from 'next/navigation'
import { useMemo, useCallback, ReactNode, MouseEvent } from 'react'
import cls from 'classnames'

interface Props {
  children: ReactNode
  className?: string
  activeClass?: string
  disabled?: boolean
  onClick?: () => void

  /**
   * Specifies the URL of the page the link goes to
   */
  to: string

  /**
   * Specifies where to open the linked document
   */
  target?: '_blank' | '_parent' | '_self' | '_top'

  /**
   * Specifies the relationship between the current document and the linked document
   */
  rel?:
    | 'alternate'
    | 'author'
    | 'bookmark'
    | 'external'
    | 'help'
    | 'license'
    | 'next'
    | 'nofollow'
    | 'noreferrer'
    | 'noopener'
    | 'prev'
    | 'search'
    | 'tag'

  /**
   * Specifies which referrer information to send with the link
   */
  referrerPolicy?:
    | 'no-referrer'
    | 'no-referrer-when-downgrade'
    | 'origin'
    | 'origin-when-cross-origin'
    | 'same-origin'
    | 'strict-origin-when-cross-origin'
    | 'unsafe-url'
}

export function Link({ children, to, ...props }: Props) {
  // __STATE's
  const router = useRouter()
  const pathname = usePathname()

  const className = useMemo(() => {
    const isActive = pathname.includes(to)
    return cls(props.className, 'router-link', {
      'router-link-active': isActive,
      [props.activeClass || '']: isActive
    })
  }, [pathname, to, props])

  // __FUNCTION's
  const handleRouterLink = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault()
      router.push(to)

      if (props?.onClick) props.onClick()
    },
    [to]
  )

  // __RENDER
  return (
    <a className={className} href={to} aria-disabled={props.disabled} onClick={handleRouterLink}>
      {children}
    </a>
  )
}
